import { getJson, postJson } from '@/lib/apiConfig';
import { XANO_AUTH_GROUP, XANO_BACKOFFICE_GROUP } from '@/lib/xanoGroups';
import type { AuthMeResponse, BackofficePingResponse, ChangePasswordBody } from '@/types/backoffice';
import { backofficeRoleLabel } from '@/types/backoffice';
import { translateApiMessage } from '@/utils/translateApiMessage';

export function normalizeAuthMe(raw: unknown): AuthMeResponse {
  const obj = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const profile =
    obj.backoffice_profile && typeof obj.backoffice_profile === 'object'
      ? (obj.backoffice_profile as Record<string, unknown>)
      : undefined;

  const backofficeRole = (obj.backoffice_role ??
    profile?.backoffice_role ??
    null) as AuthMeResponse['backoffice_role'];

  const internalDisabledRaw =
    obj.internal_disabled ?? obj.internalDisabled ?? profile?.internal_disabled;

  const isBackofficeRaw = obj.is_backoffice ?? profile?.is_backoffice;

  return {
    ...(obj as AuthMeResponse),
    id: Number(obj.id ?? 0),
    backoffice_role: backofficeRole,
    internal_disabled:
      internalDisabledRaw === undefined
        ? undefined
        : Boolean(internalDisabledRaw),
    is_backoffice:
      typeof isBackofficeRaw === 'boolean' ? isBackofficeRaw : undefined,
    must_change_password: Boolean(
      obj.must_change_password ?? profile?.must_change_password ?? false
    ),
  };
}

export function isStaffUser(me: AuthMeResponse): boolean {
  if (me.internal_disabled === true) return false;
  if (me.is_backoffice === false) return false;
  return me.backoffice_role != null;
}

export function isAdminUser(me: AuthMeResponse): boolean {
  return me.backoffice_role === 'admin_backoffice';
}

export function getStaffEntryPath(me: AuthMeResponse): string {
  return me.must_change_password ? '/backoffice/trocar-senha' : '/backoffice';
}

/** Nome legível no header — evita exibir display_name numérico (ex.: "11"). */
export function resolveStaffDisplayName(me: AuthMeResponse | null | undefined): string {
  if (!me) return 'Usuário';

  const fullName = [me.first_name, me.last_name]
    .filter(v => v != null && String(v).trim() !== '')
    .join(' ')
    .trim();
  if (fullName) return fullName;

  const display = me.display_name?.trim();
  if (display && !/^\d+$/.test(display)) return display;

  const email = me.email?.trim();
  if (email) {
    const local = email.split('@')[0]?.trim();
    if (local) return local;
  }

  return 'Usuário';
}

export function resolveStaffRoleLabel(me: AuthMeResponse | null | undefined): string {
  if (!me?.backoffice_role) return '';
  return backofficeRoleLabel(me.backoffice_role);
}

export function fetchAuthMe(options?: { preserveSession?: boolean }): Promise<AuthMeResponse> {
  return getJson<unknown>(XANO_AUTH_GROUP, 'auth/me', undefined, {
    preserveSessionOn401: options?.preserveSession === true,
  }).then(normalizeAuthMe);
}

/** Indica se o usuário pode ver/acessar o backoffice (ícone no header). */
export async function resolveHasStaffAccess(options?: {
  preserveSession?: boolean;
}): Promise<boolean> {
  const token = localStorage.getItem('authToken');
  if (!token) return false;

  let raw: unknown;
  try {
    raw = await getJson<unknown>(XANO_AUTH_GROUP, 'auth/me', undefined, {
      preserveSessionOn401: options?.preserveSession === true,
    });
  } catch {
    return false;
  }

  const me = normalizeAuthMe(raw);
  if (!me.backoffice_role) return false;
  if (!isStaffUser(me)) return false;

  const obj = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const profile =
    obj.backoffice_profile && typeof obj.backoffice_profile === 'object'
      ? (obj.backoffice_profile as Record<string, unknown>)
      : undefined;
  const hasExplicitDisabled =
    obj.internal_disabled != null ||
    obj.internalDisabled != null ||
    profile?.internal_disabled != null;

  if (hasExplicitDisabled || me.is_backoffice != null) {
    return isStaffUser(me);
  }

  try {
    const ping = await backofficePing();
    return !ping.internal_disabled;
  } catch {
    return false;
  }
}

/** auth/me logo após login — não apaga o token se a API responder 401. */
export function fetchAuthMeAfterLogin(): Promise<AuthMeResponse> {
  return fetchAuthMe({ preserveSession: true });
}

export function extractAuthToken(response: unknown): string | null {
  if (typeof response === 'string' && response.trim().length > 20) {
    return response.trim();
  }

  if (response == null || typeof response !== 'object') return null;

  const obj = response as Record<string, unknown>;
  for (const key of [
    'authToken',
    'auth_token',
    'token',
    'access_token',
    'jwt',
    'bearer',
  ]) {
    const value = obj[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }

  for (const nestedKey of ['data', 'result', 'response']) {
    const nested = obj[nestedKey];
    if (nested != null) {
      const token = extractAuthToken(nested);
      if (token) return token;
    }
  }

  return null;
}

export function isInternalDisabledApiError(error: unknown): boolean {
  const raw = getApiErrorMessageRaw(error).toLowerCase();
  return raw.includes('internal user is disabled');
}

/** Erro de login no portal — não confundir com bloqueio de UI do backoffice. */
export function getPortalLoginErrorMessage(error: unknown): string {
  if (isInternalDisabledApiError(error)) {
    return (
      'Login bloqueado pela API (função do passo 5 do auth/login no Xano, ex.: get_profile). ' +
      'Ela não pode retornar erro quando internal_disabled=true — só o backoffice deve bloquear.'
    );
  }

  const status = getApiErrorStatus(error);
  const raw = getApiErrorMessageRaw(error).toLowerCase();

  if (
    status === 403 &&
    (raw.includes('invalid credential') || raw.includes('access_denied'))
  ) {
    return 'E-mail ou senha incorretos.';
  }

  return getApiErrorMessage(error);
}

export function loginWithCredentials(
  email: string,
  password: string
): Promise<unknown> {
  return postJson(
    XANO_AUTH_GROUP,
    'auth/login',
    { email: email.trim(), password },
    { skipAuth: true }
  );
}

export function backofficePing(): Promise<BackofficePingResponse> {
  return getJson<BackofficePingResponse>(XANO_BACKOFFICE_GROUP, 'ping');
}

export function changePassword(body: ChangePasswordBody): Promise<unknown> {
  return postJson(XANO_AUTH_GROUP, 'auth/change-password', body);
}

export function clearAuthSession(): void {
  localStorage.removeItem('authToken');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('user_id');
  localStorage.removeItem('wallet');
  window.dispatchEvent(new CustomEvent('auth-expired'));
}

function getApiErrorMessageRaw(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return '';
}

export function getApiErrorMessage(error: unknown): string {
  const raw = getApiErrorMessageRaw(error) || 'Ocorreu um erro inesperado.';

  if (/weak password|please use at least \d+/i.test(raw)) {
    return translateApiMessage(raw);
  }

  const payload =
    error && typeof error === 'object' && 'payload' in error
      ? (error as { payload?: { param?: string } }).payload
      : undefined;

  if (payload?.param === 'password') {
    return 'Senha inválida: use pelo menos 8 caracteres ou deixe em branco para o sistema gerar e enviar por e-mail.';
  }

  if (payload?.param === 'new_password') {
    return 'A nova senha deve ter pelo menos 8 caracteres, ou deixe em branco para manter a atual.';
  }

  return translateApiMessage(raw);
}

export function getApiErrorStatus(error: unknown): number | undefined {
  if (error && typeof error === 'object' && 'status' in error) {
    return (error as { status?: number }).status;
  }
  return undefined;
}
